/* eslint-disable jsx-a11y/no-autofocus */
import type { ICellEditorParams } from 'ag-grid-community';

import { useTranslations } from 'next-intl';

import type { ComboboxProps, MultipleComboboxProps } from '@org/design';
import { ComboboxItem, ComboboxSection, MultipleCombobox, SingleCombobox } from '@org/design';

export interface SelectInputOption {
  label: string;
  value: string | number;
  disabled?: boolean;
  textValue?: string;
}

export interface SelectGroupInputOption {
  label: string;
  options: SelectInputOption[];
}

export type SelectInputPossibleOptions =
  | number
  | string
  | SelectInputOption
  | SelectGroupInputOption;

interface SelectCellEditorProps extends ICellEditorParams {
  onValueChange: (value: unknown) => void;
  isMulti?: boolean;
  options: SelectInputPossibleOptions[];
  disabledOptions?: string[];
  comboBoxProps?: ComboboxProps;
}

export const SelectCellEditor = (props: SelectCellEditorProps) => {
  const {
    value,
    onValueChange,
    stopEditing,
    isMulti,
    options,
    disabledOptions,
    colDef,
    comboBoxProps,
  } = props;

  const t = useTranslations();

  const optionsNodes = options.map((option) => {
    if (typeof option === 'number' || typeof option === 'string') {
      return (
        <ComboboxItem
          key={option}
          textValue={option.toString()}
        >
          {option}
        </ComboboxItem>
      );
    }
    const { label } = option;
    if ('options' in option) {
      return (
        <ComboboxSection
          key={label}
          title={label}
        >
          {option.options.map((subOption) => (
            <ComboboxItem
              key={subOption.value}
              textValue={subOption.textValue ?? subOption.label.toString()}
            >
              {subOption.label}
            </ComboboxItem>
          ))}
        </ComboboxSection>
      );
    }
    return (
      <ComboboxItem
        key={option.value}
        textValue={label.toString()}
      >
        {label}
      </ComboboxItem>
    );
  });

  if (isMulti) {
    return (
      <MultipleCombobox
        aria-label={colDef.headerName}
        autoFocus
        disabledKeys={disabledOptions}
        formatSelectedText={({ selectedItems, collection }) => {
          if (selectedItems.length === collection.size) {
            return t('common.select.allSelected');
          }
          return t('common.select.selected', { count: selectedItems.length, of: collection.size });
        }}
        isFullWidth
        onChange={(keys) => {
          // if (keys === 'all') {
          //   onValueChange(
          //     options.flatMap((currentOption) => {
          //       if (typeof currentOption === 'number' || typeof currentOption === 'string') {
          //         return [currentOption];
          //       }
          //       if ('options' in currentOption) {
          //         return currentOption.options.map((subOption) => subOption.value);
          //       }
          //       return [currentOption.value];
          //     }),
          //   );
          //   return;
          // }
          onValueChange(keys);
        }}
        value={value}
        size="sm"
        {...(comboBoxProps as MultipleComboboxProps)}
      >
        {optionsNodes}
      </MultipleCombobox>
    );
  }
  return (
    <SingleCombobox
      aria-label={colDef.headerName}
      autoFocus
      disabledKeys={disabledOptions}
      isFullWidth
      onChange={(item) => {
        onValueChange(item);
        stopEditing();
      }}
      value={value}
      size="sm"
      {...comboBoxProps}
    >
      {optionsNodes}
    </SingleCombobox>
  );
};
